import axios from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ClipboardList, Home, TreePine, Trash2, Package, User, MapPin, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { reportClientEvent } from '@/lib/clientDiagnostics';
import { BonTravailDetail } from './bon-travail/BonTravailDetail';
import { CreerBonTravailDialog } from './bon-travail/CreerBonTravailDialog';
import { EditerBonTravailDialog } from './bon-travail/EditerBonTravailDialog';
import { HeuresBudgeteesBadge } from './bon-travail/HeuresBudgeteesBadge';
import { PhotosTravail } from './PhotosTravail';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function OngletBonTravail({ dossier, onUpdate, selectedBonId, autoOpenCommande, hideAddress = false }) {
  const { token } = useAuth();
  const { canDo, canDoBonTravailAction } = usePermissions();
  const [showCreer, setShowCreer] = useState(false);
  const [showEditer, setShowEditer] = useState(null);
  const [produits, setProduits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [bonsTravail, setBonsTravail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedBon, setSelectedBon] = useState(null);
  const [prolongerState, setProlongerState] = useState({}); // {bon_id_eq_id: {date, loading}}
  const [prolongerConflict, setProlongerConflict] = useState(null);

  useEffect(() => { fetchBons(); }, [dossier.id]);

  // Ouvrir automatiquement le bon de travail si un ID est passé
  useEffect(() => {
    if (selectedBonId && bonsTravail.length > 0) {
      const bon = bonsTravail.find(b => b.id === selectedBonId);
      if (bon) {
        fetchFormData();
        setSelectedBon(bon);
      }
    }
  }, [selectedBonId, bonsTravail]);

  const fetchBons = async () => {
    setFetchError(null);
    try {
      const res = await axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`);
      if (res.status === 200) {
        const data = res.data;
        const fresh = data.bons_travail || [];
        setBonsTravail(fresh);
        // ✅ FIX 2026-04-27 : si un BT est ouvert en détail, re-synchroniser sa version
        // pour que les zones/photos fraîchement ajoutées apparaissent immédiatement.
        setSelectedBon(prev => prev ? (fresh.find(b => b.id === prev.id) || prev) : prev);
        // 🆕 [2B.3] Avertissement non-bloquant si compte mal lié (employee_link_missing).
        // Cas où le backend renvoie 200 + warning (au lieu de 403 silencieux).
        const w = data?.warning;
        if (w && w.code === 'employee_link_missing') {
          setFetchError({
            kind: 'warning',
            code: w.code,
            message: w.message || "Votre compte n'est pas lié à un employé. Contactez votre administrateur.",
          });
        }
      }
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || '';
      const msg = status === 403
        ? (detail || "Vous n'avez pas accès aux bons de travail de ce dossier.")
        : "Impossible de charger les bons de travail.";
      setFetchError({ kind: 'error', status, message: msg });
      try { toast.error(msg, { duration: 5000 }); } catch { /* */ }
      // 🆕 [2A.3] Télescope : on capture les échecs silencieux qui rendent
      // l'onglet "Bon de Travail" vide sans aucun feedback visible.
      try {
        reportClientEvent('api_error', 'warning', {
          context: 'OngletBonTravail.fetchBons',
          dossier_id: dossier?.id || '',
          status: e?.response?.status || 0,
          error: String(e?.message || e || '').slice(0, 200),
        });
      } catch { /* silencieux */ }
    }
    finally { setLoading(false); }
  };

  const fetchFormData = async () => {
    const [pRes, eRes, eqRes] = await Promise.all([
      axios.get(`${API_URL}/api/produits`),
      axios.get(`${API_URL}/api/employees`),
      axios.get(`${API_URL}/api/equipements-inventaire`),
    ]);
    if (pRes.status === 200) { const d = pRes.data; setProduits(d.produits || []); }
    if (eRes.status === 200) { const d = eRes.data; setEmployees(d.employees || []); }
    if (eqRes.status === 200) { const d = eqRes.data; setEquipements(d.equipements?.filter(eq => eq.actif) || []); }
  };

  const handleOpenCreer = () => { fetchFormData(); setShowCreer(true); };

  const handleOpenEditer = (bon) => { fetchFormData(); setShowEditer(bon); };

  const handleCreated = () => { setShowCreer(false); setShowEditer(null); fetchBons(); if (onUpdate) onUpdate(); };

  const handleDelete = async (bonId) => {
    if (!window.confirm('Supprimer ce bon de travail ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/crm/bons-travail/${bonId}`);
      if (res.status === 200) { toast.success('Bon supprimé'); if (selectedBon?.id === bonId) setSelectedBon(null); fetchBons(); if (onUpdate) onUpdate(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleToggleEtape = async (bon, etapeIdx) => {
    const updatedEtapes = [...(bon.etapes || [])];
    const etape = updatedEtapes[etapeIdx];
    etape.completee = !etape.completee;
    etape.date_completion = etape.completee ? new Date().toISOString() : null;
    etape.completee_par = etape.completee ? 'Chef d\'équipe' : null;

    try {
      const res = await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`, { ...bon, etapes: updatedEtapes });
      if (res.status === 200) { toast.success('Étape mise à jour'); fetchBons(); }
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" /></div>;

  const handleProlongerEquipement = async (bonId, equipementId, dateAjout) => {
    const key = `${bonId}_${equipementId}`;
    setProlongerState(s => ({ ...s, [key]: { date: dateAjout, loading: true } }));
    try {
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bonId}/equipements/prolonger`, { equipement_id: equipementId, date_ajout: dateAjout });
      const data = res.data;
      if (data.status === 'conflit') {
        setProlongerConflict({ message: data.message, dossier_nom: data.conflit_dossier_nom });
      } else if (data.status === 'success') {
        toast.success(data.message);
        await fetchBons();
        setSelectedBon(prev => prev ? { ...prev, equipements: data.equipements } : prev);
      } else {
        toast.error('Erreur lors de la prolongation');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setProlongerState(s => { const n = {...s}; delete n[key]; return n; });
    }
  };

  // If a bon is selected, show detail view
  if (selectedBon) {
    const bon = bonsTravail.find(b => b.id === selectedBon.id) || selectedBon;
    return (
      <>
        <BonTravailDetail 
          bon={bon} 
          dossier={dossier}
          employees={employees}
          onBack={() => setSelectedBon(null)} 
          onDelete={() => handleDelete(bon.id)} 
          onToggleEtape={(idx) => handleToggleEtape(bon, idx)} 
          onEdit={() => handleOpenEditer(bon)}
          onRefresh={fetchBons}
          onProlongerEquipement={handleProlongerEquipement}
          prolongerState={prolongerState}
          autoOpenCommande={autoOpenCommande}
          hideAddress={hideAddress}
        />
        {prolongerConflict && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Conflit d'équipement</h3>
                  <p className="text-sm text-gray-500">La date demandée n'est pas disponible</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">{prolongerConflict.message}</p>
              <Button onClick={() => setProlongerConflict(null)} className="w-full">Compris</Button>
            </div>
          </div>
        )}
        {showEditer && (
          <EditerBonTravailDialog 
            open={!!showEditer} 
            onClose={() => setShowEditer(null)} 
            dossier={dossier} 
            bon={showEditer}
            produits={produits} 
            employees={employees} 
            equipements={equipements} 
            onSaved={handleCreated} 
          />
        )}
      </>
    );
  }

  // Séparer BT intérieurs et extérieurs
  const bonsInterieur = bonsTravail.filter(b => b.type_travaux === 'interieur');
  const bonsExterieur = bonsTravail.filter(b => b.type_travaux === 'exterieur');

  // Permissions (bt_* sont dans bon_travail_actions)
  const canCreerBT = canDoBonTravailAction('bt_creer');
  const canSupprimerBT = canDoBonTravailAction('bt_supprimer_liste');
  const canVoirPhotos = canDoBonTravailAction('bt_voir_photos_travail');

  return (
    <div className="space-y-4">
      {/* 🆕 [2B.3] Bannière visible pour les erreurs / warnings de chargement */}
      {fetchError && (
        <div
          className={`p-3 rounded-md border text-sm ${
            fetchError.kind === 'warning'
              ? 'bg-amber-50 border-amber-300 text-amber-900'
              : 'bg-red-50 border-red-300 text-red-800'
          }`}
          data-testid="onglet-bt-error-banner"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="font-semibold mb-1">
                {fetchError.kind === 'warning' ? '⚠️ Avertissement' : '❌ Erreur de chargement'}
              </p>
              <p className="text-xs">{fetchError.message}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setLoading(true); fetchBons(); }}
              data-testid="onglet-bt-retry-btn"
            >
              Réessayer
            </Button>
          </div>
        </div>
      )}

      {canCreerBT && (
        <div className="flex justify-end">
          <Button onClick={handleOpenCreer} className="bg-green-700 hover:bg-green-800" data-testid="btn-creer-bon">
            <Plus className="w-4 h-4 mr-2" /> Créer un Bon de Travail
          </Button>
        </div>
      )}

      {bonsTravail.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Aucun bon de travail pour ce dossier</p>
          {canCreerBT && (
            <Button onClick={handleOpenCreer}><Plus className="w-4 h-4 mr-2" /> Créer un Bon de Travail</Button>
          )}
        </CardContent></Card>
      ) : (
        bonsTravail.map((bon) => (
          <BonTravailCard 
            key={bon.id} 
            bon={bon} 
            onClick={() => setSelectedBon(bon)} 
            onDelete={canSupprimerBT ? () => handleDelete(bon.id) : null} 
          />
        ))
      )}

      {/* Section Photos de Travail */}
      {canVoirPhotos && (
        <PhotosTravail 
          dossier={dossier} 
          bonsInterieur={bonsInterieur}
          bonsExterieur={bonsExterieur}
          onUpdate={() => {
            // ✅ FIX 2026-04-27 : après création d'une zone (ou ajout/suppression photo),
            // on rafraîchit la liste locale des BT pour que les nouvelles zones apparaissent
            // sans nécessiter un reload complet de la page.
            fetchBons();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showCreer && (
        <CreerBonTravailDialog open={showCreer} onClose={() => setShowCreer(false)} dossier={dossier} produits={produits} employees={employees} equipements={equipements} onCreated={handleCreated} />
      )}
    </div>
  );
}

/* ─── Carte résumé ─── */
function BonTravailCard({ bon, onClick, onDelete }) {
  const isExt = bon.type_travaux === 'exterieur';
  const TypeIcon = isExt ? TreePine : Home;
  const nbProduits = bon.produits?.length || 0;
  const nbEtapes = bon.etapes?.length || 0;
  const etapesCompleted = (bon.etapes || []).filter(e => e.completee).length;
  const nbZones = bon.zones?.length || 0;

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick} data-testid={`bon-card-${bon.id}`}>
      <CardContent className="pt-5">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-lg ${isExt ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
              <TypeIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">Bon de Travail — {isExt ? 'Extérieur' : 'Intérieur'}</h3>
                <Badge variant="outline" className="text-xs">{bon.statut}</Badge>
              </div>
              <p className="text-sm text-gray-600 line-clamp-1">{(bon.description_travaux || 'Aucune description').replace(/<[^>]+>/g, ' ').substring(0, 100)}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 items-center">
                {bon.chef_equipe_nom && <span className="flex items-center gap-1"><User className="w-3 h-3" />{bon.chef_equipe_nom}</span>}
                <span>{nbProduits} produit{nbProduits !== 1 ? 's' : ''}</span>
                <span>{etapesCompleted}/{nbEtapes} étape{nbEtapes !== 1 ? 's' : ''}</span>
                {nbZones > 0 && <span>{nbZones} zone{nbZones !== 1 ? 's' : ''}</span>}
                <HeuresBudgeteesBadge bon={bon} />
              </div>
            </div>
          </div>
          {onDelete && (
            <Button variant="ghost" size="sm" className="hidden sm:flex" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
